/* eslint-disable @typescript-eslint/no-explicit-any */
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { createMigrate, persistStore, persistReducer } from 'redux-persist';
import { FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import autoMergeLevel1 from 'redux-persist/es/stateReconciler/autoMergeLevel1';
import authReducer from './reducers/authReducer';
import accountReducer from './reducers/accountReducer';
import themeReducer from './reducers/themeReducer';
import uploadFilesReducer from './reducers/uploadReducer';
import recentCaseReducer from './reducers/recentCaseReducer';
import caseReducer from './reducers/caseReducer';
import caseDataReducer from './reducers/caseDataReducer';
import partyReducer from './reducers/partyReducer';
import userReducer from './reducers/userReducer';
import activeBillableTimerReducer from './reducers/activeBillableTimerReducer';
import termsAndPoliciesReducer from './reducers/termsAndPoliciesReducer';
import engagementTemplatesReducer from './reducers/engagementTemplateReducer';
import caseTemplatesReducer from './reducers/caseTemplateReducer';
import caseDocumentReducer from './reducers/caseDocumentReducer';
import caseEngagementReducer from './reducers/caseEngagementReducer';
import caseEventReducer from './reducers/caseEventReducer';
import caseExpenseReducer from './reducers/caseExpenseReducer';
import caseMilestoneReducer from './reducers/caseMilestoneReducer';
import caseNoteReducer from './reducers/caseNoteReducer';
import caseTaskReducer from './reducers/caseTaskReducer';
import timeTrackingReducer from './reducers/timeTrackingReducer';
import caseInvoiceReducer from './reducers/caseInvoiceReducer';
import notificationsReducer from './reducers/notificationReducer';
import agentReducer from './reducers/aiAgentReducer';
import insightsReducer from './reducers/insightsReducer';
import clientPortalReducer from './reducers/clientPortalReducer';

const themePersistConfig = { key: 'theme', storage, stateReconciler: autoMergeLevel1 };

const rootReducer = combineReducers({
    auth: authReducer,
    theme: persistReducer(themePersistConfig, themeReducer as any),
    accounts: accountReducer,
    recentCases: recentCaseReducer,
    cases: caseReducer,
    caseData: caseDataReducer,
    users: userReducer,
    parties: partyReducer,
    upload: uploadFilesReducer,
    activeBillableTimer: activeBillableTimerReducer,
    termsAndPolicies: termsAndPoliciesReducer,
    engagementTemplates: engagementTemplatesReducer,
    caseTemplates: caseTemplatesReducer,
    caseDocuments: caseDocumentReducer,
    caseEngagement: caseEngagementReducer,
    caseEvents: caseEventReducer,
    caseExpenses: caseExpenseReducer,
    caseMilestones: caseMilestoneReducer,
    caseNotes: caseNoteReducer,
    caseTasks: caseTaskReducer,
    caseInvoices: caseInvoiceReducer,
    timeTrackingEntries: timeTrackingReducer,
    notifications: notificationsReducer,
    agent: agentReducer,
    insights: insightsReducer,
    clientPortal: clientPortalReducer,
});

const persistedReducer = persistReducer(
    {
        key: 'root',
        version: 2,
        storage,
        migrate: createMigrate({
            1: (state) => state ? { ...state, cases: { case: null } } : state,
            2: (state) => state ? {
                ...state,
                accounts: { userAccounts: [] },
                users: { users: [] },
                clientPortal: { portals: [], users: [] },
            } : state,
        }),
        stateReconciler: autoMergeLevel1,
        blacklist: ['accounts', 'users', 'clientPortal'],
    },
    rootReducer as any
);

const configStore = () => {
    return configureStore({
        reducer: persistedReducer,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                serializableCheck: {
                    ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
                },
            }),
    });
}

const reduxStore = configStore();
const persistor = persistStore(reduxStore);

const store = { reduxStore, persistor };

export default store;

export type RootState = ReturnType<typeof rootReducer>
export type AppDispatch = typeof reduxStore.dispatch
